// pages/reports/IncomeReportPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  InputGroup,
  Table,
  Spinner,
  Pagination,
} from "react-bootstrap";
import {
  Download,
  RefreshCcw,
  Search,
  ChevronUp,
  ChevronDown,
} from "react-feather";

import * as XLSX from "xlsx";

import { fetchIncomeSourcesThunk } from "../../../store/slices/incomeExpenseSlice";

import {
  fetchIncomeReportThunk,
  clearIncomeReport,
} from "../../../store/slices/incomeExpenseReportSlice";


import { printTableReport } from "./reportPrintUtils";

import "./ReportPage.css";

const formatIndianDateTime = (v) => {
  const s = String(v || "").trim();
  if (!s) return "-";

  const onlyDate = /^\d{4}-\d{2}-\d{2}$/.test(s);

  if (onlyDate) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }

  const isoLike = s.includes("T") ? s : s.replace(" ", "T");
  const d = new Date(isoLike);

  if (Number.isNaN(d.getTime())) return s;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();

  let hh = d.getHours();
  const min = String(d.getMinutes()).padStart(2, "0");
  const ampm = hh >= 12 ? "PM" : "AM";

  hh = hh % 12;
  hh = hh === 0 ? 12 : hh;

  return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
};

const safeAmount = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

export default function IncomeReportPage() {
  const dispatch = useDispatch();

  const incomeSources = useSelector(
    (s) => s.incomeExpense?.incomeSources || []
  );

  const { incomeRows = [], loading, error } = useSelector(
    (s) => s.incomeExpenseReport || {}
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [status, setStatus] = useState("all");
  const [incomeSourceId, setIncomeSourceId] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [search, setSearch] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    dispatch(fetchIncomeSourcesThunk());
  }, [dispatch]);

  const handleSort = (field) => {
    setSortField((currentField) => {
      if (currentField === field) {
        setSortDirection((currentDirection) =>
          currentDirection === "asc" ? "desc" : "asc"
        );

        return currentField;
      }

      setSortDirection("asc");
      return field;
    });

    setPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <span className="ms-1 report-sort-default-icon">
          <ChevronUp size={11} />
          <ChevronDown size={11} />
        </span>
      );
    }

    return sortDirection === "asc" ? (
      <ChevronUp size={14} className="ms-1" />
    ) : (
      <ChevronDown size={14} className="ms-1" />
    );
  };

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    const searchedRows = !q
      ? [...incomeRows]
      : (incomeRows || []).filter((r) => {
          const searchableText = `
            ${r.income_id}
            ${r.date}
            ${r.income_source_name}
            ${r.income_source_id}
            ${r.amount}
            ${r.approval_status}
            ${r.approved}
            ${r.approved_by_name}
            ${r.approved_by}
            ${r.remarks}
          `.toLowerCase();

          return searchableText.includes(q);
        });

    if (!sortField) {
      return searchedRows;
    }

    const numericFields = [
      "income_id",
      "amount",
    ];

    return [...searchedRows].sort((a, b) => {
      let aValue;
      let bValue;

      if (sortField === "income_source") {
        aValue = a.income_source_name || a.income_source_id || "";
        bValue = b.income_source_name || b.income_source_id || "";
      } else if (sortField === "approval_status") {
        aValue =
          a.approval_status ||
          (a.approved ? "approved" : "pending");

        bValue =
          b.approval_status ||
          (b.approved ? "approved" : "pending");
      } else if (sortField === "approved_by") {
        aValue = a.approved_by_name ?? a.approved_by ?? "";
        bValue = b.approved_by_name ?? b.approved_by ?? "";
      } else {
        aValue = a?.[sortField];
        bValue = b?.[sortField];
      }

      if (numericFields.includes(sortField)) {
        aValue = Number(aValue || 0);
        bValue = Number(bValue || 0);

        return sortDirection === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (sortField === "date") {
        const aTime = aValue ? new Date(
          String(aValue).includes("T")
            ? aValue
            : String(aValue).replace(" ", "T")
        ).getTime() : 0;

        const bTime = bValue ? new Date(
          String(bValue).includes("T")
            ? bValue
            : String(bValue).replace(" ", "T")
        ).getTime() : 0;

        return sortDirection === "asc"
          ? aTime - bTime
          : bTime - aTime;
      }

      const comparison = String(aValue || "").localeCompare(
        String(bValue || ""),
        undefined,
        {
          numeric: true,
          sensitivity: "base",
        }
      );

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [incomeRows, search, sortField, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [
    fromDate,
    toDate,
    status,
    incomeSourceId,
    minAmount,
    maxAmount,
    search,
    incomeRows.length,
    sortField,
    sortDirection,
  ]);

  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  const handleLoad = () => {
    dispatch(
      fetchIncomeReportThunk({
        from_date: fromDate || "",
        to_date: toDate || "",
        status: status || "all",
        income_source_id: incomeSourceId || "",
        min_amount: minAmount || "",
        max_amount: maxAmount || "",
        q: "",
      })
    );
  };

  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setStatus("all");
    setIncomeSourceId("");
    setMinAmount("");
    setMaxAmount("");
    setSearch("");
    setSortField("");
    setSortDirection("asc");
    setPage(1);

    dispatch(clearIncomeReport());
  };

  const exportExcel = () => {
    const data = filteredRows.map((r, idx) => ({
      "Sl No.": idx + 1,
      "Date & Time": formatIndianDateTime(r.date),
      "Income Source":
        r.income_source_name ||
        r.income_source_id ||
        "-",
      Amount: safeAmount(r.amount),
      Status:
        r.approval_status ||
        (r.approved ? "approved" : "pending"),
      "Approved By":
        r.approved_by_name ??
        r.approved_by ??
        "-",
      Remarks: r.remarks || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(
      wb,
      ws,
      "Income Report"
    );

    XLSX.writeFile(
      wb,
      "income_report.xlsx"
    );
  };

  const exportPDF = () => {
    const data = filteredRows.map((r, idx) => ({
      "Sl No.": idx + 1,
      "Date & Time": formatIndianDateTime(r.date),
      "Income Source": r.income_source_name || r.income_source_id || "-",
      Amount: `₹ ${safeAmount(r.amount)}`,
      Status: r.approval_status || (r.approved ? "approved" : "pending"),
      "Approved By": r.approved_by_name ?? r.approved_by ?? "-",
      Remarks: r.remarks || "",
    }));

    printTableReport({
      title: "Income - Report",
fileName: "income_report.pdf",
      data,
      metaLines: [`Total Records: ${filteredRows.length}`],
    });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    const maxButtons = 5;

    let start = Math.max(
      1,
      currentPage -
        Math.floor(maxButtons / 2)
    );

    let end = Math.min(
      totalPages,
      start + maxButtons - 1
    );

    start = Math.max(
      1,
      end - maxButtons + 1
    );

    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() =>
          setPage((p) =>
            Math.max(1, p - 1)
          )
        }
      />
    );

    for (
      let p = start;
      p <= end;
      p++
    ) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === currentPage}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={
          currentPage === totalPages
        }
        onClick={() =>
          setPage((p) =>
            Math.min(
              totalPages,
              p + 1
            )
          )
        }
      />
    );

    return (
      <Pagination className="mb-0">
        {items}
      </Pagination>
    );
  };

  const sortableHeader = (
    label,
    field,
    style
  ) => (
    <th style={style}>
      <button
        type="button"
        className={`report-sort-btn ${
          sortField === field
            ? "is-active"
            : ""
        }`}
        onClick={() =>
          handleSort(field)
        }
      >
        {label}
        {renderSortIcon(field)}
      </button>
    </th>
  );

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                Income - Report
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          {error ? (
            <div className="alert alert-danger mb-3">
              {error}
            </div>
          ) : null}

          <div className="report-filter-box">
            <Row className="g-3">
              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  From Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) =>
                    setFromDate(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  To Date
                </Form.Label>

                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) =>
                    setToDate(
                      e.target.value
                    )
                  }
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Status
                </Form.Label>

                <Form.Select
                  value={status}
                  onChange={(e) =>
                    setStatus(
                      e.target.value
                    )
                  }
                >
                  <option value="all">
                    All
                  </option>

                  <option value="pending">
                    Pending
                  </option>

                  <option value="approved">
                    Approved
                  </option>
                </Form.Select>
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Income Source
                </Form.Label>

                <Form.Select
                  value={incomeSourceId}
                  onChange={(e) =>
                    setIncomeSourceId(
                      e.target.value
                    )
                  }
                >
                  <option value="">
                    All
                  </option>

                  {incomeSources.map((x) => (
                    <option
                      key={x.income_source_id}
                      value={String(
                        x.income_source_id
                      )}
                    >
                      {x.income_source_name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Min Amount
                </Form.Label>

                <Form.Control
                  value={minAmount}
                  onChange={(e) =>
                    setMinAmount(
                      e.target.value
                    )
                  }
                  placeholder="0"
                />
              </Col>

              <Col lg={3} md={6}>
                <Form.Label className="report-label">
                  Max Amount
                </Form.Label>

                <Form.Control
                  value={maxAmount}
                  onChange={(e) =>
                    setMaxAmount(
                      e.target.value
                    )
                  }
                  placeholder="10000"
                />
              </Col>

              <Col
                lg={6}
                md={12}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    onClick={handleLoad}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    onClick={handleClear}
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              Income List{" "}
              <span className="report-count">
                ({total.toLocaleString()} records)
              </span>
            </div>

            <div className="report-table-right">
              <InputGroup className="report-search">
                <InputGroup.Text>
                  <Search size={16} />
                </InputGroup.Text>

                <Form.Control
                  value={search}
                  onChange={(e) =>
                    setSearch(
                      e.target.value
                    )
                  }
                  placeholder="Search source / amount / remarks..."
                />
              </InputGroup>

              <Button
                className="report-btn"
                variant="outline-primary"
                onClick={exportExcel}
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                onClick={exportPDF}
                disabled={
                  !filteredRows.length
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    {sortableHeader(
                      "Sl No.",
                      "income_id",
                      { width: 100 }
                    )}

                    {sortableHeader(
                      "Date & Time",
                      "date",
                      { width: 170 }
                    )}

                    {sortableHeader(
                      "Income Source",
                      "income_source"
                    )}

                    {sortableHeader(
                      "Amount",
                      "amount",
                      { width: 140 }
                    )}

                    {sortableHeader(
                      "Status",
                      "approval_status",
                      { width: 120 }
                    )}

                    {sortableHeader(
                      "Approved By",
                      "approved_by",
                      { width: 160 }
                    )}

                    {sortableHeader(
                      "Remarks",
                      "remarks"
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading...
                      </td>
                    </tr>
                  ) : pagedRows.length ? (
                    pagedRows.map(
                      (r, idx) => (
                        <tr
                          key={String(
                            r.income_id ||
                              idx
                          )}
                        >
                          <td>
                            {(currentPage - 1) *
                              pageSize +
                              idx +
                              1}
                          </td>

                          <td>
                            {formatIndianDateTime(
                              r.date
                            )}
                          </td>

                          <td>
                            {r.income_source_name ||
                              r.income_source_id ||
                              "-"}
                          </td>

                          <td>
                            ₹ {safeAmount(r.amount)}
                          </td>

                          <td>
                            {r.approval_status ||
                              (r.approved
                                ? "approved"
                                : "pending")}
                          </td>

                          <td>
                            {r.approved_by_name ??
                              r.approved_by ??
                              "-"}
                          </td>

                          <td>
                            {r.remarks || "-"}
                          </td>
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>Rows:</span>

                <Form.Select
                  value={pageSize}
                  onChange={(e) => {
                    setPage(1);
                    setPageSize(
                      Number(
                        e.target.value
                      )
                    );
                  }}
                  className="page-size-select"
                >
                  {[10, 20, 50, 100].map(
                    (n) => (
                      <option
                        key={n}
                        value={n}
                      >
                        {n}
                      </option>
                    )
                  )}
                </Form.Select>
              </div>

              <div className="report-pagination">
                {renderPagination()}
              </div>

              <div className="report-page-info">
                Page {currentPage} /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}
